import { useState } from 'react'

import { Box, Card, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { EmployeeTimeDetailChartDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { PickerSelector } from 'modules/core/components/DateNavigator/_DateRangeSelector/_PickerSelector'
import { Popover } from 'modules/core/components/DateNavigator/_Popover'
import Filter from 'modules/core/components/Filter'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  DetailTransformedData,
  getDetailTransformedData,
} from 'modules/project_management/components/charts/DistributionChart/getDistributionTransformedData'
import { ResponsiveBarChart } from 'modules/project_management/components/charts/ResponsiveBarChart'
import { useGetSelectedGraphDates } from 'modules/project_management/hooks/graphs/useGetSelectedGraphDates'
import {
  overviewFilterOptionsEnum,
  overviewFrequencyOptionsEnum,
} from 'modules/project_management/lib/constants'
import { MINUTES_PER_HOUR, formatProjectHours } from 'modules/project_management/lib/formatHours'
import {
  maxLengthOfLabelsArray,
  overviewFilterWholeProject,
} from 'modules/project_management/lib/overviewOptions'
import {
  OverviewFilterOption,
  currentWeekFilterOption,
  overviewFilterOptions,
  overviewFrequencyOption,
  overviewFrequencyOptions,
} from 'modules/project_management/lib/overviewOptions'

interface EmployeeViewDistributionChartProps {
  employeeId: string
  employeeName: string
}

const EmployeeViewDistributionChart = ({
  employeeId,
  employeeName,
}: EmployeeViewDistributionChartProps) => {
  const [toggle, setToggle] = useToggle(false)
  const [customStartDate, setCustomStartDate] = useState<Date>(new Date())
  const [customEndDate, setCustomEndDate] = useState<Date>(new Date())
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [frequencyPopoverOpen, setFrequencyPopoverOpen] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState<OverviewFilterOption | undefined>(
    currentWeekFilterOption()
  )
  const [selectedFrequency, setSelectedFrequency] = useState<overviewFrequencyOption | undefined>(
    overviewFrequencyOptions()[0]
  )

  const hasCustomRange =
    selectedFilter?.value === overviewFilterOptionsEnum.customRange &&
    customStartDate &&
    customEndDate
  const customRange = {
    startDate: moment(customStartDate),
    endDate: moment(customEndDate),
    slotType: selectedFrequency?.value,
  }
  const filteredRanges = useGetSelectedGraphDates(selectedFilter?.value)

  const selectedDates = hasCustomRange ? customRange : filteredRanges
  const selectedFrecuency = selectedFrequency?.value || overviewFrequencyOptionsEnum.daily

  const { loading, error, data } = useQuery(EmployeeTimeDetailChartDocument, {
    variables: {
      employeeId: employeeId,
      startDate: selectedDates.startDate.format('YYYY-MM-DD'),
      endDate: selectedDates.endDate.format('YYYY-MM-DD'),
      slotType: selectedFrecuency,
    },
  })

  const estimatedWorkHours = data?.attendance?.estimatedWorkHours.nodes || []
  const employeeTimeDetailChart =
    data?.projectManagement?.employeeTimeDetailChartsConnection.nodes || []

  const totalEstimatedWorkHours = estimatedWorkHours.reduce(
    (acc, item) => acc + +item.regularMinutes / 60,
    0
  )
  const totalEstimatedWorkHoursInMinutes = totalEstimatedWorkHours * MINUTES_PER_HOUR

  const totalImputedMinutes = employeeTimeDetailChart.reduce(
    (acc, item) => acc + (item.imputedMinutes || 0),
    0
  )

  const trackedHoursVsEstimatedHours = `${formatProjectHours(
    totalImputedMinutes
  )} / ${formatProjectHours(+Math.round(totalEstimatedWorkHoursInMinutes).toFixed(2))}`

  const handleOpenPopover = useUnsafeCallback(() => {
    setPopoverOpen(true)
  }, [])
  const handleClosePopover = useUnsafeCallback(() => {
    setPopoverOpen(false)
  }, [])
  const handleCloseFrequencyPopover = useUnsafeCallback(() => {
    setFrequencyPopoverOpen(false)
  }, [])
  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setCustomStartDate(startDate)
    setCustomEndDate(endDate)
    handleClosePopover()
  }

  const findOption = (value: string | undefined) => {
    if (value === overviewFilterOptionsEnum.customRange) {
      handleOpenPopover()
    }
    return overviewFilterOptions().find((option) => option.value === value)
  }
  const findFrequency = (value: string | undefined) => {
    return overviewFrequencyOptions().find((option) => option.value === value)
  }

  useEffect(() => {
    track(
      trackingEvents.projectManagement.flexibleTracking.overview.myProjectsOverviewFilterSelected,
      {
        filterType: selectedFilter?.value,
      }
    )
  }, [selectedFilter?.value])

  const maxLabels = maxLengthOfLabelsArray(selectedFrecuency)
  const transformedData: DetailTransformedData = getDetailTransformedData(
    employeeTimeDetailChart,
    'projects',
    selectedFrecuency
  )

  return (
    <>
      <Box paddingTop="s16" flexDirection="row" justifyContent="spaceBetween">
        <Box flexDirection="row" justifyContent="flexStart" gap="s8">
          <Popover
            open={popoverOpen}
            onClose={handleClosePopover}
            trigger={
              <Filter.SingleChoice
                label={i18n.t('common.date_date')}
                options={overviewFilterOptions()}
                defaultOpen={false}
                selectedOption={selectedFilter}
                onValueChange={(value) => setSelectedFilter(findOption(value))}
              />
            }
          >
            <PickerSelector
              startDate={customStartDate}
              endDate={customEndDate}
              onDateRangeChange={handleDateRangeChange}
            />
          </Popover>
          <Popover
            open={frequencyPopoverOpen}
            onClose={handleCloseFrequencyPopover}
            trigger={
              <Filter.SingleChoice
                label={i18n.t('project_management.graphs.details.frequency')}
                options={overviewFrequencyOptions()}
                defaultOpen={false}
                selectedOption={selectedFrequency}
                onValueChange={(value) => setSelectedFrequency(findFrequency(value))}
              />
            }
          />
        </Box>
      </Box>
      <Card>
        <Card.Content>
          <Box paddingY="s8" gap="s16" width="full">
            <Box flexDirection="row" justifyContent="spaceBetween" width="full">
              <Box maxWidth="s600" flexDirection="row" alignItems="center" gap="s6">
                <Text.Header2 weight="normal">
                  {i18n.t('project_management.graphs.details.title')}
                </Text.Header2>
                <Text.Header2 weight="normal">{employeeName}</Text.Header2>
              </Box>
            </Box>
            <Box width="full">
              <Text color="grey1000" size="300" ellipsis whiteSpace="nowrap">
                {selectedFilter?.label || overviewFilterWholeProject().label}
              </Text>
              <Box flexDirection="row" alignItems="center" gap="s8">
                <Text color="grey1000" size="500" weight="semibold" whiteSpace="nowrap">
                  {trackedHoursVsEstimatedHours}
                </Text>
                <Tooltip.Alt
                  open={toggle}
                  toggle={setToggle}
                  attachment="middle left"
                  label={i18n.t(
                    'project_management.my_projects.overview.distribution_chart.comparative_hours_tooltip'
                  )}
                >
                  <Icon.Medium icon="info-circle-line" color="grey800" />
                </Tooltip.Alt>
              </Box>
            </Box>
            <Text color="grey800" weight="medium" size="300">
              {selectedFilter?.subtitle || overviewFilterWholeProject().subtitle}
            </Text>
            <Box paddingBottom="s32">
              <ResponsiveBarChart
                detailData={transformedData}
                unit={i18n.t('project_management.graphs.evolution.time.unit')}
                maxLabels={maxLabels}
                loading={loading}
                error={error}
              />
            </Box>
          </Box>
        </Card.Content>
      </Card>
    </>
  )
}

export default EmployeeViewDistributionChart
